import {
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  useQuery as useApolloQuery,
} from '@apollo/client';
import { DocumentNode } from 'apollo-link/lib/types';
import { useEffect } from 'react';

import { useToasts } from '@sbu/contexts.toasts';

import { checkError } from 'shared/components/utils/graphql/GraphqlErrorHandler';

type NotifyErrors<TData, TVariables extends OperationVariables> = (
  result: QueryResult<TData, TVariables>
) => boolean;

type QueryOptions<TData, TVariables extends OperationVariables> = {
  notifyErrors?: NotifyErrors<TData, TVariables> | boolean;
};

export const useQuery = <
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  query: DocumentNode,
  options?: QueryHookOptions<TData, TVariables> &
    QueryOptions<TData, TVariables>
): QueryResult<TData, TVariables> => {
  const { notifyErrors = true } = options || {};
  const { addError } = useToasts();
  const result = useApolloQuery<TData, TVariables>(query, {
    errorPolicy: 'all',
    ...(options || {}),
  });

  useEffect(() => {
    if (!result.loading && result.error) {
      if (
        typeof notifyErrors === 'function' ? notifyErrors(result) : notifyErrors
      ) {
        addError(checkError(result.error));
      }

      // Stop polling (if applicable) when an error is encountered.
      result.stopPolling();
    }
  }, [result, options, addError, notifyErrors]);

  return result;
};
